import { DICTIONARY_API_KEYS, API_BASE_URL } from '../config';

interface DictionaryResult {
  definition: string;
  thesaurus?: {
    synonyms: string[];
    antonyms: string[];
  };
  learners?: string;
  intermediate?: string;
}

async function fetchFromAPI(word: string, type: keyof typeof DICTIONARY_API_KEYS) {
  const key = DICTIONARY_API_KEYS[type];
  const endpoint = type === 'thesaurus' ? 'thesaurus' : 'collegiate';
  
  const response = await fetch(
    `${API_BASE_URL}/${endpoint}/json/${word}?key=${key}`
  );
  return await response.json();
}

export async function fetchDefinition(word: string): Promise<DictionaryResult> {
  try {
    const [collegiate, thesaurus, learners, intermediate] = await Promise.allSettled([
      fetchFromAPI(word, 'collegiate'),
      fetchFromAPI(word, 'thesaurus'),
      fetchFromAPI(word, 'learners'),
      fetchFromAPI(word, 'intermediate'),
    ]);

    const result: DictionaryResult = {
      definition: '',
    };

    // Process collegiate dictionary result
    if (collegiate.status === 'fulfilled' && Array.isArray(collegiate.value)) {
      if (collegiate.value.length === 0) {
        throw new Error('No definition found');
      }

      if (typeof collegiate.value[0] === 'string') {
        throw new Error('Word not found. Did you mean: ' + collegiate.value.slice(0, 3).join(', ') + '?');
      }

      const definitions = collegiate.value
        .flatMap((entry: any) => entry.shortdef?.map((def: string) => def) || [])
        .filter(Boolean);

      if (definitions.length > 0) {
        result.definition = definitions[0];
      }
    }

    // Process thesaurus result
    if (thesaurus.status === 'fulfilled' && Array.isArray(thesaurus.value) && thesaurus.value[0]?.meta) {
      const syns = thesaurus.value[0].meta.syns?.[0] || [];
      const ants = thesaurus.value[0].meta.ants?.[0] || [];
      result.thesaurus = {
        synonyms: syns,
        antonyms: ants,
      };
    }

    // Process learners dictionary result
    if (learners.status === 'fulfilled' && Array.isArray(learners.value)) {
      const learnerDef = learners.value[0]?.shortdef?.[0];
      if (learnerDef) {
        result.learners = learnerDef;
      }
    }

    // Process intermediate dictionary result
    if (intermediate.status === 'fulfilled' && Array.isArray(intermediate.value)) {
      const intermediateDef = intermediate.value[0]?.shortdef?.[0];
      if (intermediateDef) {
        result.intermediate = intermediateDef;
      }
    }

    if (!result.definition && !result.learners && !result.intermediate) {
      throw new Error('No definitions found in any dictionary');
    }

    // Prioritize definitions
    result.definition = result.definition || result.learners || result.intermediate;

    return result;
  } catch (error) {
    throw error;
  }
}